import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UserApi } from "../api/UserApi";
import { useDebouncedCallback } from "use-debounce";
import { auth } from "../config/firebase";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../context/AppContext";

export type SetUsernamePageProps = {
};

const USERNAME_REGEX = /^[a-z0-9._+-]{3,25}$/i;
const MIN_LENGTH = 3;

const checkUsernameAvailability = async (username: string): Promise<boolean> => {
  const isAvailable = await UserApi.isUsernameAvailable(username);
  return isAvailable;
}

export default function SetUsernamePage({ }: SetUsernamePageProps) {
  const { data, setData } = useAppData();
  const [username, setUsername] = useState<string>(data.user.username || auth.currentUser?.email?.split("@")[0] || "");
  const availableRef = useRef<Record<string, boolean>>({});
  const [available, setAvailable] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);
  const navigate = useNavigate();

  const isValidFormat = useMemo(() => USERNAME_REGEX.test(username || ""), [username]);
  const canSubmit = isValidFormat && availableRef.current[username] === true && !submitting;

  useEffect(() => {
    if (data.user.username) {
      setUsername(data.user.username);
    }
  }, [data.user.username]);
  
  const checkUsername = useDebouncedCallback(
    async (username) => {
      if (username.length < MIN_LENGTH) {
        setAvailable(null);
        return
      }
      const canCheck = USERNAME_REGEX.test(username || "") && 
        username.length >= MIN_LENGTH &&
        username?.trim() !== data.user.username?.trim();

      if (!canCheck) return;
      setError(null);
      try {
        const ok = await checkUsernameAvailability(username.trim());
        availableRef.current[username] = ok;
        setAvailable(ok);
      } catch (event) {
        setError("Whoops, banana slip! Please try again.");
        setAvailable(null);
      }
    },
    150,
    { leading: true },
  )

  useEffect(() => {
    checkUsername(username.trim());
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    try {
      // Final availability re-check (defensive)
      const ok = await checkUsernameAvailability(username.trim());
      if (!ok) {
        setAvailable(false);
        availableRef.current[username] = ok;
        setError("That username was just claimed, try another one.");
        return;
      }
      await UserApi.setUsername(username.trim());
      setData(prev => ({ ...prev, user: { ...prev.user, username: username.trim() } }));
      toast.success(`Saved! Welcome, @${username.trim()}`);
      navigate("/tuttobanana")
    } catch (event) {
      setError("Could not save right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <title>Set Your Tutto Username | Tuttobanana</title>
      <meta
        name="description"
        content="Pick your Tutto username to join the Bananaverse."
      />

      <div className="min-h-screen flex items-start justify-center bg-gray-100 px-6 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full max-w-md"
        >
          <div className="w-full bg-white shadow-md rounded-lg p-8">
            <h1 className="text-2xl font-bold mb-2 text-center text-gray-900">Tutto's Username</h1>
            {!data.user.username && <p className="text-gray-600 mb-6 text-center">
              First, let's save your <span className="font-semibold">Tutto's username</span>, your handle in the Bananaverse.
            </p>}

            {error && (
              <div className="mb-4 text-sm text-red-600 bg-red-100 border border-red-300 px-4 py-2 rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">@</span>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    inputMode="text"
                    autoCapitalize="none"
                    autoComplete="off"
                    spellCheck={false}
                    value={username}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      checkUsername(event.target.value);
                      setUsername(event.target.value);
                      setAvailable(null);
                    }}
                    onBlur={() => setTouched(true)}
                    placeholder="tutto.name"
                    className="w-full rounded-md border border-gray-300 pl-8 pr-12 py-2.5 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 outline-none transition"
                    aria-describedby="username-help username-status"
                  />
                </div>
                <p id="username-help" className="mt-2 text-sm text-gray-500">
                  3-25 chars. Letters, numbers, dots, underscore, plus and dash only.
                </p>

                {/* Status / validation messages */}
                <div id="username-status" className="mt-2 text-sm">
                  {touched && !isValidFormat && username.length >= MIN_LENGTH && (
                    <p className="text-red-600">Use 3-25 letters, numbers, "+", "-", "." or "_".</p>
                  )}
                  {availableRef.current[username] === true && isValidFormat && (
                    <p className="text-green-700">
                      Nice! <span className="font-semibold">@{username.trim()}</span> is available.
                    </p>
                  )}
                  {availableRef.current[username] === false && (
                    <p className="text-red-600">
                      Sorry, that one's taken.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-gray-500 text-center sm:text-left">Tip: Keep it short and banana-sweet. 🍌</div>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-5 py-2.5 shadow transition ${
                    !canSubmit ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  {submitting ? "Saving…" : "Save Username"}
                </button>
              </div>
            </form>

            {/* Footer hint */}
            <div className="text-center text-xs text-gray-500 mt-4">
              By saving, you agree to play nice in the Bananaverse.
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}